
import Vue, { PropType } from 'vue';

import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import TextStyle from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import FontFamily from '@tiptap/extension-font-family';
import { FontSize } from './extensions/extension-font-size';
import Color from '@tiptap/extension-color';
import TiptapToolbar from './TiptapToolbar.vue';
import { CvIconButton, CvButton } from '@carbon/vue';
import { colorArrayToColorRGBA } from '@/utils';

import Close20 from '@carbon/icons-vue/es/close/20';
import { useStore as useShapeStore } from '@/stores/shape';
import { useStore as useProductStore } from '@/stores/product';
import { useStore as useHistoryStore } from '@/stores/history';
import { useStore as useColosManagementStore } from '@/stores/colorsManagement';
import { useStore as useNotificationStore } from '@/stores/notification';
import { mapState, mapActions } from 'pinia';
import { generateEventPayload } from '@/utils/generatePriceParamsEventPayload';
import cloneDeep from 'lodash.clonedeep';

export type CustomEditorAttributes = {
  fontSize: number;
  fontSizePT: number;
  fontFamily: string;
  fontWeight: string;
  fontStyle: string;
  fontVariant: string;
  lineHeight: number;
  color: [number, number, number, number?];
  textAlign: string;
  text: string;
  textHtml: string;
  colorName: string;
};

export default Vue.extend({
  components: {
    EditorContent,
    TiptapToolbar,
    CvIconButton,
    CvButton,
    Close20,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
    attributes: {
      type: Object as PropType<CustomEditorAttributes>,
      required: true,
    },
  },

  data() {
    return {
      editor: null as unknown as Editor,
      // store original attributes on mounted, so we can revert to them in case of cancelling the editor
      originalAttributes: {} as CustomEditorAttributes,
      // store previewed attributes so we :
      // - don't update it again on apply if nothing was changed
      // - revert to original attributes if preview occurred
      previewAttributes: {} as CustomEditorAttributes,
    };
  },

  computed: {
    ...mapState(useColosManagementStore, ['availablePmsColors']),
    ...mapState(useProductStore, [
      'selectedPrintPosition',
      'isLaserConverter',
      'isDebossingTechnique',
    ]),
    ...mapState(useShapeStore, ['shapes', 'activeShapeId']),
    originalImageWebDpi(): number {
      return this.selectedPrintPosition.originalImageWebDpi;
    },
  },

  mounted() {
    this.editor = new Editor({
      content: this.attributes.textHtml,
      extensions: [
        TextStyle,
        TextAlign.configure({
          types: ['paragraph'],
          alignments: ['left', 'center', 'right'],
        }),
        Color.configure({
          types: ['textStyle'],
        }),
        FontFamily.configure({
          types: ['textStyle'],
        }),
        FontSize.configure({
          types: ['textStyle'],
        }),
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3],
          },
          paragraph: {
            HTMLAttributes: {
              class: 'w2p-text',
              id: 'text',
            },
          },
        }),
      ],
      onUpdate: ({ editor }) => {
        const docContent = editor.getJSON().content;

        const finalText = docContent
          ? docContent
              ?.map((c) => {
                return c.content
                  ? c.content
                      ?.map((p) => p.text as string)
                      .reduce((prev, curr) => prev + curr)
                  : '';
              })
              .reduce((prev, curr) => prev + '\n' + curr)
          : '';

        if (finalText.length === 0) {
          if (this.attributes.fontWeight === 'bold') {
            this.editor.chain().focus().toggleBold().run();
          }
          if (this.attributes.fontStyle === 'italic') {
            this.editor.chain().focus().toggleItalic().run();
          }
          const [r, g, b, a] = this.attributes.color;
          this.editor
            .chain()
            .focus()
            .setFontFamily(this.attributes.fontFamily)
            .setColor(`rgba(${r}, ${g}, ${b}, ${a ? a : 1})`)
            .run();

          this.editor
            .chain()
            .focus()
            .setFontSize(`${this.attributes.fontSizePT}pt`)
            .run();
        }
      },
    });
    this.init();

    this.originalAttributes = cloneDeep(this.attributes);
  },

  beforeDestroy() {
    this.editor.destroy();
  },

  methods: {
    ...mapActions(useShapeStore, [
      'removeTextPayload',
      'updateShapeStateFromSnapshot',
    ]),
    ...mapActions(useHistoryStore, ['recordHistory', 'undo']),
    ...mapActions(useNotificationStore, ['revalidateColorsOnly']),

    updateAttributes({
      fontFamily,
      colorName,
      fontWeight,
      fontStyle,
      textAlign,
      fontSizePT,
    }: CustomEditorAttributes & { colorName: string }) {
      if (textAlign) {
        this.attributes.textAlign = textAlign;
        this.editor.chain().focus().selectAll().setTextAlign(textAlign).run();
      }

      if (fontWeight) {
        this.editor.chain().focus().selectAll().toggleBold().run();
        this.attributes.fontWeight = this.editor.isActive('bold')
          ? 'bold'
          : 'normal';
      }

      if (fontStyle) {
        this.editor.chain().focus().selectAll().toggleItalic().run();
        this.attributes.fontStyle = this.editor.isActive('italic')
          ? 'italic'
          : 'normal';
      }

      if (fontFamily) {
        this.attributes.fontFamily = fontFamily;
        this.editor.chain().focus().selectAll().setFontFamily(fontFamily).run();
      }

      if (fontSizePT) {
        this.attributes.fontSizePT = fontSizePT;
        this.editor
          .chain()
          .focus()
          .selectAll()
          .setFontSize(`${fontSizePT}pt`)
          .run();
      }

      if (colorName) {
        const foundColor = this.availablePmsColors.find(
          (pms) => pms.name === colorName
        );

        if (foundColor) {
          const { red: r, green: g, blue: b } = foundColor;
          const colorRGBString = `rgb(${r},${g},${b})`;

          this.attributes.colorName = colorName;
          this.attributes.color = [
            foundColor.red,
            foundColor.green,
            foundColor.blue,
            foundColor.colorFuzz,
          ];

          this.editor
            .chain()
            .focus()
            .selectAll()
            .setColor(colorRGBString)
            .setFontSize(`${this.attributes.fontSizePT}pt`)
            .run();
        }
        const eventPayload = generateEventPayload();

        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });
      }
    },

    /**
     * Cancel out any changes done by preview actions
     */
    cancelSave() {
      if (this.originalAttributes.text === '') {
        this.removeTextPayload(this.id, false);
        const notificationSore = useNotificationStore();
        notificationSore.revalidateColorsOnly();
      } else if (this.previewAttributes?.text) {
        this.$emit('update:attributes', {
          id: this.id,
          ...this.originalAttributes,
          repositionText: false,
        });
      }

      this.$emit('text-editor:close');
    },

    applyText(previewOnly: boolean) {
      const finalText = this.editor.getText();
      this.attributes.text = finalText;
      this.attributes.textHtml = this.editor.getHTML();

      // if the text is being edited, we don't want to reposition it
      const repositionText = this.originalAttributes.textHtml === '';

      // store preview attributes and update pintura ff something was changed
      if (previewOnly) {
        if (
          finalText &&
          JSON.stringify(this.attributes) !==
            JSON.stringify(this.originalAttributes) &&
          JSON.stringify(this.attributes) !==
            JSON.stringify(this.previewAttributes)
        ) {
          this.previewAttributes = cloneDeep(this.attributes);
          this.$emit('update:attributes', {
            id: this.id,
            ...this.attributes,
            repositionText: repositionText,
          });
        }
      }
      // if attributes were updated in editor or after preview, update pintura
      else if (finalText) {
        if (
          JSON.stringify(this.attributes) !==
            JSON.stringify(this.originalAttributes) &&
          JSON.stringify(this.attributes) !==
            JSON.stringify(this.previewAttributes)
        ) {
          this.$emit('update:attributes', {
            id: this.id,
            ...this.attributes,
            repositionText: repositionText,
          });
        }
        this.$emit('text-editor:close');
        // remove shape if no text
      } else {
        this.removeTextPayload(this.id, false);
        const notificationSore = useNotificationStore();
        notificationSore.revalidateColorsOnly();
        this.$emit('text-editor:close');
      }
    },

    /**
     * Bring editor into a state declared by attributes previously set on text
     * shape
     */
    init() {
      this.editor.commands.selectAll();

      this.attributes.fontWeight === 'bold' && this.editor.commands.setBold();

      this.attributes.fontStyle === 'italic' &&
        this.editor.commands.setItalic();

      this.attributes.fontFamily &&
        this.editor.commands.setFontFamily(this.attributes.fontFamily);

      this.attributes.textAlign &&
        this.editor.commands.setTextAlign(this.attributes.textAlign);

      if (this.attributes.fontSizePT) {
        this.editor
          .chain()
          .focus()
          .selectAll()
          .setFontSize(`${this.attributes.fontSizePT}pt`)
          .run();
      }

      let [r, g, b, a] = this.attributes.color;
      if (this.isDebossingTechnique() || this.isLaserConverter()) {
        [r, g, b, a] = [255, 255, 255, 1];
        this.attributes.color = [255, 255, 255, 1];
      }
      this.editor.commands.setColor(`rgba(${r}, ${g}, ${b}, ${a ? a : 1})`);
    },
  },
});
